import getPermitFieldMetadata from "./permitFieldMetadata";
import { useMemo, useState, useEffect } from "react";
import ImportWizardContextProvider from "components/importer/ImportWizardContext";
import {
  createPermit,
  importPermits,
  saveImportColumnMappings,
} from "api/permits";
import { getEntityPermitTypes } from "api";
import {
  capitalize,
  formatLicensePlateString,
  formatPhoneNumber,
  formatVehicleString,
  getBlankPermit,
  getBlankPermitType,
  getShortFormattedAddress,
  logError,
} from "utils";
import {
  EntityTypes,
  FailedImport,
  ImportWizardActionHandler,
  Permit,
  PermitField,
  PermitStatus,
  TraxeroActionResponse,
  CreateEntityParams,
  IdentifyDuplicatesParams,
  Property,
  PermitType,
  PanelRootContext,
} from "types";
import { ActionFunction, useLocation, useOutletContext, useNavigate, useLoaderData } from "react-router";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import ImportWizard from "components/importer/ImportWizard";
import { useFetcherKey } from "hooks/useFetcherKey";
import { MRT_ColumnDef } from "material-react-table";
import PermitCorrectionForm from "./PermitCorrectionForm";
import PermitTypeWidget from "./PermitTypeWidget";
import FormHeaderBar from "components/layout/FormHeaderBar";
import { routeWithParams } from "router";
import { ROUTES } from "router/routes";
import useTenantID from "hooks/useTenantID";
import useColors from "hooks/useColors";

export interface PermitImportWizardRouteParams {
  params: {
    tenantID: string;
    propertyID: string;
    groupID: string;
  };
}

export const permitImportWizardLoader = async ({
  params,
}: PermitImportWizardRouteParams): Promise<{ _permitTypes: PermitType[] }> => {
  const { tenantID, propertyID, groupID } = params;
  const permitTypes = await getEntityPermitTypes({
    tenantID,
    entityType: propertyID ? EntityTypes.Property : EntityTypes.PropertyGroup,
    entityID: propertyID ? parseInt(propertyID, 10) : parseInt(groupID, 10),
  });
  return { _permitTypes: permitTypes };
};

export default function PermitImportWizard() {
  const { t } = useTranslation();
  const tenantID = useTenantID();
  const colors = useColors();
  const { fetcherKey } = useFetcherKey();
  const { pathname } = useLocation();
  const { _permitTypes } = useLoaderData<{ _permitTypes: PermitType[] }>();
  const navigate = useNavigate();

  const { entity: property } = useOutletContext<PanelRootContext<Property>>();
  const [permitTypes, setPermitTypes] = useState<PermitType[]>([]);
  const [permitType, setPermitType] = useState<PermitType | null>(null);

  // Load permit types
  useEffect(() => {
    const loadPermitTypes = async () => {
      const pt = await _permitTypes;
      setPermitTypes(pt);
      if (pt.length > 0) {
        setPermitType(pt[0]);
      }
    };
    loadPermitTypes();
  }, []);

  const onBack = () => {
    navigate(routeWithParams(ROUTES.PROPERTY_PERMITS, [tenantID, property.id]));
  };
  const propertyAddress = {
    street: property.location.street,
    city: property.location.city,
    state: property.location.stateCode,
    postalCode: property.location.postalCode,
  };

  function createPermit({ row, mappings }: CreateEntityParams): Permit {
    const blankPermit = getBlankPermit(tenantID);
    const permit = { ...blankPermit };

    Object.entries(mappings).forEach(([permitField, csvHeader]) => {
      if (csvHeader === "Leave Blank") {
        return;
      }
      const value = row[csvHeader];
      switch (permitField as PermitField) {
        case "permit.permitStatus":
          permit.permitStatus = value.toLowerCase() as PermitStatus;
          break;
        case "permit.startTime":
          permit.startTime = dayjs(value).valueOf();
          break;
        case "permit.endTime":
          permit.endTime = dayjs(value).valueOf();
          break;
        case "permit.vehicle.make":
          permit.vehicle.make = value.toUpperCase();
          break;
        case "permit.vehicle.model":
          permit.vehicle.model = value;
          break;
        case "permit.vehicle.year":
          if (value) {
            permit.vehicle.year = parseInt(value);
          }
          break;
        case "permit.vehicle.color":
          permit.vehicle.color = value.toLowerCase();
          break;
        case "permit.vehicle.vin":
          permit.vehicle.vin = value.toUpperCase();
          break;
        case "permit.vehicle.plate.state":
          permit.vehicle.plate.state = value.toUpperCase();
          break;
        case "permit.vehicle.plate.number":
          permit.vehicle.plate.number = value.toUpperCase();
          break;
        case "permit.contact.name":
          permit.contact.name = value.trim();
          break;
        case "permit.contact.primaryEmail":
          permit.contact.primaryEmail = value.trim();
          break;
        case "permit.contact.primaryPhone":
          permit.contact.primaryPhone = value.replace(/\D/g, "");
          break;
        case "permit.contact.location.street":
          permit.contact.location.street = value;
          break;
        case "permit.contact.location.city":
          permit.contact.location.city = value;
          break;
        case "permit.contact.location.unit":
          permit.contact.location.unit = value;
          break;
        case "permit.contact.location.stateCode":
          permit.contact.location.stateCode = value.toUpperCase();
          break;
        case "permit.contact.location.postalCode":
          permit.contact.location.postalCode = value;
          break;
      }
    });

    return permit;
  }
  function identifyDuplicates({
    columnMappings,
    data,
  }: IdentifyDuplicatesParams): number[] {
    // the backend will check for duplicates in existing permits
    // but we need to remove duplicates from the csv data
    const duplicates: number[] = [];
    const csvColumnWithPlateNumber =
      columnMappings["permit.vehicle.plate.number"];
    if (csvColumnWithPlateNumber) {
      const plateSet: Set<string> = new Set();
      const csvColumnWithPlateState =
        columnMappings["permit.vehicle.plate.state"];
      const csvColumnWithPermitStatus = columnMappings["permit.permitStatus"];

      data.forEach((row, index) => {
        const permitStatus = row[csvColumnWithPermitStatus];
        if (permitStatus?.toLowerCase() !== PermitStatus.Active) {
          // only check active permits for duplicates
          return;
        }
        // Normalize plate number by removing all non-alphanumeric characters for comparison
        const normalizedPlateNumber = row[csvColumnWithPlateNumber]
          ? row[csvColumnWithPlateNumber].replace(/[^a-zA-Z0-9]/g, "")
          : "";
        const plateKey =
          `${normalizedPlateNumber}-${row[csvColumnWithPlateState]}`.toLowerCase();
        if (plateSet.has(plateKey)) {
          duplicates.push(index);
        } else {
          plateSet.add(plateKey);
        }
      });
    }
    return duplicates;
  }

  const columns = useMemo<MRT_ColumnDef<Permit>[]>(() => {
    return [
      {
        header: t("permit.contact.name", { ns: "entities" }),
        accessorKey: "contact.name",
      },
      {
        header: t("permit.contact.primaryEmail", { ns: "entities" }),
        accessorKey: "contact.primaryEmail",
      },
      {
        header: t("permit.contact.primaryPhone", { ns: "entities" }),
        id: "contact.primaryPhone",
        accessorFn: (row) => formatPhoneNumber(row.contact.primaryPhone),
      },
      {
        header: t("permit.vehicle.plate.label", { ns: "entities" }),
        id: "vehicle.plate.label",
        accessorFn: (row) => formatLicensePlateString(row.vehicle.plate),
      },
      {
        header: t("permit.vehicle.label", { ns: "entities" }),
        id: "vehicle.label",
        accessorFn: (row) => formatVehicleString(row.vehicle),
      },
      {
        header: t("address"),
        id: "contact.location.label",
        accessorFn: (row) => getShortFormattedAddress(row.contact.location),
      },
      {
        header: t("permit.startTime", { ns: "entities" }),
        id: "startTime",
        accessorFn: (row) => dayjs(row.startTime).format("MM/DD/YYYY hh:mm a"),
      },
      {
        header: t("permit.endTime", { ns: "entities" }),
        id: "endTime",
        accessorFn: (row) => dayjs(row.endTime).format("MM/DD/YYYY hh:mm a"),
      },
      {
        header: t("permit.permitStatus", { ns: "entities" }),
        id: "permitStatus",
        accessorFn: (row) => capitalize(row.permitStatus),
      },
    ];
  }, [t]);

  return (
    <ImportWizardContextProvider<Permit, PermitField>
      metadata={getPermitFieldMetadata(colors)}
      createEntity={createPermit}
      identifyDuplicates={identifyDuplicates}
      entityName={t("permit.label", { ns: "entities" })}
      defaultAddress={propertyAddress}
    >
      <FormHeaderBar />
      <ImportWizard<Permit>
        fetcherKey={fetcherKey}
        actionRoute={pathname}
        onBack={onBack}
        maxHeight={`calc(100dvh - 346px)`}
        previewColumns={columns}
        CorrectionForm={PermitCorrectionForm}
        UploadStepRightComponent={
          <PermitTypeWidget
            options={permitTypes}
            permitType={permitType || getBlankPermitType(tenantID)}
            setPermitType={setPermitType}
          />
        }
        _permitType={permitType || getBlankPermitType(tenantID)}
      />
    </ImportWizardContextProvider>
  );
}

export const permitWizardAction: ActionFunction = async ({
  params,
  request,
}): Promise<
  TraxeroActionResponse<ImportWizardActionHandler, FailedImport<Permit>[]>
> => {
  const { body, handler, permitTypeID } = await request.json();
  const { tenantID, propertyID, groupID } =
    params as PermitImportWizardRouteParams["params"];

  let entityType: EntityTypes;
  let entityID: number;
  if (propertyID) {
    entityType = EntityTypes.Property;
    entityID = parseInt(propertyID, 10);
  } else if (groupID) {
    entityType = EntityTypes.PropertyGroup;
    entityID = parseInt(groupID, 10);
  } else {
    entityType = EntityTypes.None;
    entityID = 0;
  }

  let res: Response;
  try {
    switch (handler) {
      case ImportWizardActionHandler.SUBMIT_ENTITY_DATA:
        res = await importPermits({
          tenantID,
          entityType,
          entityID,
          permitTypeID,
          body,
        });
        break;
      case ImportWizardActionHandler.CREATE_ENTITY:
        res = await createPermit({
          tenantID,
          entityType,
          entityID,
          permitTypeID,
          body,
        });
        break;
      case ImportWizardActionHandler.SAVE_COLUMN_MAPPINGS:
        res = await saveImportColumnMappings({
          tenantID,
          entityID,
          entityType,
          permitTypeID,
          body,
        });
        break;
      default:
        res = new Response(null, {
          status: 500,
          statusText: "unknown handler",
        });
    }
  } catch (error) {
    logError(error as Error);
    return {
      handler,
      status: 500,
      statusText: "Internal Server Error",
    };
  }

  if (
    res.status === 200 &&
    handler === ImportWizardActionHandler.SUBMIT_ENTITY_DATA
  ) {
    const failedPermits = await res.json();
    return {
      handler,
      status: res.status,
      statusText: res.statusText,
      body: failedPermits,
    };
  }

  return {
    handler,
    status: res.status,
    statusText: res.statusText,
  };
};
